import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import BottomNav from '@/components/feature/BottomNav';
import { places, categoryTabs, filterTags } from '@/mocks/placesData';
import { useTrip } from '@/context/TripContext';

export default function Explore() {
  const navigate = useNavigate();
  const { selectedSpots, toggleSpot, isInTrip } = useTrip();
  const [activeCategory, setActiveCategory] = useState('全部');
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [toast, setToast] = useState('');

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2000);
  };

  const toggleFilter = (tag: string) => {
    setActiveFilters((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
    );
  };

  const handleAddToTrip = (e: React.MouseEvent, placeId: string) => {
    e.stopPropagation();
    const added = toggleSpot(placeId);
    if (added) {
      showToast('已加入行程');
    } else {
      showToast('已在行程中');
    }
  };

  const filteredPlaces = useMemo(() => {
    return places.filter((place) => {
      const matchCategory =
        activeCategory === '全部' || place.category === activeCategory;
      const matchFilter =
        activeFilters.length === 0 ||
        activeFilters.some((f) => place.status.includes(f) || place.tags.includes(f));
      return matchCategory && matchFilter;
    });
  }, [activeCategory, activeFilters]);

  return (
    <div className="min-h-screen bg-bg pb-20">
      {/* Toast */}
      {toast && (
        <div className="fixed top-12 left-0 right-0 z-50 flex justify-center pointer-events-none">
          <div className="bg-text-primary/95 text-white text-sm px-4 py-2 rounded-full shadow-lg backdrop-blur-sm">
            {toast}
          </div>
        </div>
      )}

      {/* Header */}
      <header className="sticky top-0 z-40 bg-bg px-4 pt-3 pb-2">
        <h1 className="text-xl font-bold text-text-primary">找地方</h1>
        <p className="text-sm text-text-muted mt-1">发现安吉好吃好玩的地点</p>
        <div className="flex items-center gap-2 px-3 py-2.5 bg-white rounded-lg border border-divider mt-3 cursor-pointer">
          <i className="ri-search-line text-text-muted" />
          <span className="text-sm text-text-muted">搜景点、咖啡、民宿、亲子</span>
        </div>
      </header>

      {/* Category Tabs */}
      <div className="mt-2">
        <div className="flex gap-2 px-4 overflow-x-auto no-scrollbar">
          {categoryTabs.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveCategory(tab)}
              className={`whitespace-nowrap px-3.5 py-1.5 rounded-full text-sm font-medium cursor-pointer transition-colors ${
                activeCategory === tab
                  ? 'bg-bamboo text-white'
                  : 'bg-white text-text-body border border-divider'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {/* Filter Tags */}
      <div className="mt-3 px-4">
        <div className="flex gap-2 overflow-x-auto no-scrollbar">
          {filterTags.map((tag) => {
            const active = activeFilters.includes(tag);
            return (
              <button
                key={tag}
                onClick={() => toggleFilter(tag)}
                className={`whitespace-nowrap px-3 py-1 rounded-full text-xs cursor-pointer transition-colors ${
                  active
                    ? 'bg-bamboo-pale text-bamboo border border-bamboo/20'
                    : 'bg-white text-text-muted border border-divider'
                }`}
              >
                {active && <i className="ri-check-line mr-0.5" />}
                {tag}
              </button>
            );
          })}
        </div>
      </div>

      {/* Place Cards */}
      <div className="mt-4 space-y-0">
        {filteredPlaces.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-text-muted">
            <i className="ri-search-line text-4xl mb-3" />
            <p className="text-sm">没有找到符合条件的地点</p>
            <button
              className="mt-3 text-bamboo text-sm cursor-pointer"
              onClick={() => {
                setActiveCategory('全部');
                setActiveFilters([]);
              }}
            >
              清除筛选
            </button>
          </div>
        ) : (
          filteredPlaces.map((place, i) => (
            <div key={place.id}>
              {place.recommended && (i === 2 || i === 4) && (
                <div className="px-4 py-1.5">
                  <span className="text-[10px] font-medium text-stream bg-[#E3F0F3] px-2 py-0.5 rounded">
                    {place.recommendedType || '推荐'}
                  </span>
                </div>
              )}
              <div
                className="bg-white rounded-xl overflow-hidden mx-4 mb-3 cursor-pointer"
                onClick={() => navigate(`/place/${place.id}`)}
              >
                <div className="relative h-44">
                  <img
                    src={place.images[0]}
                    alt={place.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-2.5 right-2.5 flex gap-1.5">
                    <button
                      className={`w-8 h-8 rounded-full bg-white/80 flex items-center justify-center cursor-pointer`}
                      onClick={(e) => {
                        e.stopPropagation();
                        showToast(`导航至${place.name}`);
                      }}
                    >
                      <i className="ri-map-pin-line text-text-body text-sm" />
                    </button>
                    <button
                      className={`w-8 h-8 rounded-full flex items-center justify-center cursor-pointer transition-colors ${
                        isInTrip(place.id)
                          ? 'bg-bamboo/20'
                          : 'bg-white/80'
                      }`}
                      onClick={(e) => handleAddToTrip(e, place.id)}
                    >
                      <i
                        className={`text-sm ${
                          isInTrip(place.id)
                            ? 'ri-check-line text-bamboo'
                            : 'ri-add-circle-line text-bamboo'
                        }`}
                      />
                    </button>
                  </div>
                </div>
                <div className="p-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-base font-bold text-text-primary">{place.name}</h3>
                      <p className="text-xs text-text-muted mt-0.5">
                        {place.area} · {place.category}
                      </p>
                    </div>
                    <span className="text-sm font-medium text-bamboo">{place.price}</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {place.tags.map((tag) => (
                      <span
                        key={tag}
                        className="text-[10px] px-2 py-0.5 rounded bg-bamboo-pale text-bamboo"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-text-muted">
                      <i className="ri-time-line mr-1" />
                      {place.duration}
                    </span>
                    {isInTrip(place.id) && (
                      <span className="text-[10px] text-bamboo flex items-center gap-1">
                        <i className="ri-check-line" />
                        已加入行程
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <BottomNav />
    </div>
  );
}